import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

export interface TurnstileWidgetHandle {
  reset: () => void
}

export interface TurnstileWidgetProps {
  siteKey: string
  onToken?: (token: string) => void
  onError?: (error: unknown) => void
  onExpire?: () => void
  size?: 'flexible' | 'compact' | 'normal'
  theme?: 'light' | 'dark' | 'auto'
  appearance?: 'always' | 'execute' | 'interaction-only'
  className?: string
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      remove: (widgetId: string) => void
      reset: (widgetId: string) => void
    }
  }
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget({ siteKey, onToken, onError, onExpire, size = 'flexible', theme = 'light', appearance = 'always', className = '' }, ref) {
  const widgetRef = useRef<HTMLDivElement>(null)
  const [widgetId, setWidgetId] = useState<string | null>(null)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    if (window.turnstile) {
      setIsScriptLoaded(true)
      return
    }

    const existingScript = document.querySelector('script[src*="turnstile"]')
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkLoaded)
          if (mountedRef.current) {
            setIsScriptLoaded(true)
          }
        }
      }, 100)

      return () => {
        clearInterval(checkLoaded)
        mountedRef.current = false
      }
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.onload = () => {
      if (mountedRef.current) {
        setIsScriptLoaded(true)
      }
    }
    document.head.appendChild(script)

    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!isScriptLoaded || !siteKey || !widgetRef.current || widgetId !== null) {
      return
    }

    const timer = setTimeout(() => {
      if (!mountedRef.current || !widgetRef.current) return

      try {
        const id = window.turnstile!.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            if (onToken && mountedRef.current) onToken(token)
          },
          'error-callback': (error: unknown) => {
            if (onError && mountedRef.current) onError(error)
          },
          'expired-callback': () => {
            if (onExpire && mountedRef.current) onExpire()
          },
          size,
          theme,
          appearance,
        })

        setWidgetId(id)
      } catch (error) {
        console.error('Error creating Turnstile widget:', error)
      }
    }, 100)

    return () => {
      clearTimeout(timer)
    }
  }, [isScriptLoaded, siteKey, onToken, onError, onExpire, size, theme, appearance])

  useEffect(() => {
    return () => {
      if (widgetId !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId)
        } catch (e) {
          console.error('Error removing widget:', e)
        }
      }
    }
  }, [widgetId])

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetId !== null && window.turnstile) {
        try {
          window.turnstile.reset(widgetId)
        } catch (e) {
          console.error('Error resetting Turnstile widget:', e)
        }
      }
    },
  }), [widgetId])

  if (!siteKey) {
    console.warn('Turnstile site key not configured')
    return null
  }

  return (
    <div
      ref={widgetRef}
      className={`turnstile-widget ${className}`}
    />
  )
})

export default TurnstileWidget
